import {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  UNSAFE_NavigationContext,
  createPath,
  useLocation,
  useNavigate,
  type Navigator,
  type To,
} from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useRightSidebar } from "@/contexts/RightSidebarContext";
import {
  PanelControllerContext,
  type PanelController,
  type UtilityPanel,
} from "./panel";
import { PANEL_PARAM, splitHref, withPanelParam } from "./paths";

export interface PanelLocationState {
  /** Depth of the panel's own history at this entry (R5). */
  panelDepth?: number;
}

function panelParamOf(to: To) {
  const search =
    typeof to === "string" ? splitHref(to).search : (to.search ?? "");
  return new URLSearchParams(search).get(PANEL_PARAM);
}

/**
 * Owns the side panel state for the main layout: the `panel` URL parameter
 * (R3, R4), the utility panels (R7) and the unsaved-changes guard (C7).
 * Also guards main-view navigation that would drop a dirty panel.
 */
export function PanelProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const navigation = useContext(UNSAFE_NavigationContext);
  const { isOpen, activeTab, openToTab, closeSidebar } = useRightSidebar();
  const panelPath =
    new URLSearchParams(location.search).get(PANEL_PARAM) || null;

  const locationRef = useRef(location);
  locationRef.current = location;
  const panelPathRef = useRef(panelPath);
  panelPathRef.current = panelPath;

  const dirtyChecks = useRef(new Set<() => boolean>());
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const registerDirty = useCallback((isDirty: () => boolean) => {
    dirtyChecks.current.add(isDirty);
    return () => {
      dirtyChecks.current.delete(isDirty);
    };
  }, []);

  const confirmDiscard = useCallback((action: () => void) => {
    const dirty =
      panelPathRef.current !== null &&
      [...dirtyChecks.current].some((isDirty) => isDirty());
    if (dirty) setPendingAction(() => action);
    else action();
  }, []);

  const mainUrlWithPanel = useCallback((panel: string | null) => {
    const { pathname, search, hash } = locationRef.current;
    return withPanelParam(pathname, search, hash, panel);
  }, []);

  const open = useCallback<PanelController["open"]>(
    (href, options = {}) => {
      if (href === panelPathRef.current) return;
      confirmDiscard(() => {
        closeSidebar();
        const state: PanelLocationState = { panelDepth: 1 };
        navigate(mainUrlWithPanel(href), { replace: options.replace, state });
      });
    },
    [closeSidebar, confirmDiscard, mainUrlWithPanel, navigate],
  );

  const close = useCallback(() => {
    if (!panelPathRef.current) return;
    confirmDiscard(() => navigate(mainUrlWithPanel(null)));
  }, [confirmDiscard, mainUrlWithPanel, navigate]);

  const expand = useCallback(() => {
    const target = panelPathRef.current;
    if (!target) return;
    confirmDiscard(() => navigate(target));
  }, [confirmDiscard, navigate]);

  const openInMain = useCallback(
    (href: string) => {
      const { pathname, search, hash } = splitHref(href);
      navigate(withPanelParam(pathname, search, hash, panelPathRef.current));
    },
    [navigate],
  );

  const openUtility = useCallback(
    (utility: UtilityPanel) => {
      const show = () => openToTab(utility);
      if (panelPathRef.current) {
        confirmDiscard(() => {
          navigate(mainUrlWithPanel(null));
          show();
        });
      } else {
        show();
      }
    },
    [confirmDiscard, mainUrlWithPanel, navigate, openToTab],
  );

  // Entity content and utility panels share one panel (R7): with a panel in
  // the URL, the utility panel is hidden.
  const utility = !panelPath && isOpen ? (activeTab as UtilityPanel) : null;

  const controller = useMemo<PanelController>(
    () => ({
      panelPath,
      utility,
      open,
      close,
      expand,
      openInMain,
      openUtility,
      closeUtility: closeSidebar,
      confirmDiscard,
      registerDirty,
    }),
    [
      panelPath,
      utility,
      open,
      close,
      expand,
      openInMain,
      openUtility,
      closeSidebar,
      confirmDiscard,
      registerDirty,
    ],
  );

  // Main-view navigation and the panel:
  // - staying on the same page (filters, search params) keeps the panel open,
  //   even when the page rewrites all its search params;
  // - leaving the page drops the panel, asking first when it has unsaved
  //   changes (C7).
  const guardedNavigation = useMemo(() => {
    const inner = navigation.navigator;
    const adjust = (to: To): To => {
      const current = panelPathRef.current;
      if (!current || panelParamOf(to) !== null) return to;
      const target = typeof to === "string" ? splitHref(to) : to;
      const { pathname } = locationRef.current;
      if ((target.pathname ?? pathname) !== pathname) return to;
      const params = new URLSearchParams(target.search ?? "");
      params.set(PANEL_PARAM, current);
      return { ...target, pathname, search: `?${params.toString()}` };
    };
    const guard = (to: To, run: (to: To) => void) => {
      const next = adjust(to);
      if (panelPathRef.current && panelParamOf(next) !== panelPathRef.current) {
        confirmDiscard(() => run(next));
      } else {
        run(next);
      }
    };
    const navigator: Navigator = {
      createHref: (to) => inner.createHref(to),
      encodeLocation: inner.encodeLocation
        ? (to) => inner.encodeLocation!(to)
        : undefined,
      go: (delta) => inner.go(delta),
      push: (to, state, opts) =>
        guard(to, (next) => inner.push(next, state, opts)),
      replace: (to, state, opts) =>
        guard(to, (next) => inner.replace(next, state, opts)),
    };
    return { ...navigation, navigator };
  }, [navigation, confirmDiscard]);

  return (
    <PanelControllerContext.Provider value={controller}>
      <UNSAFE_NavigationContext.Provider value={guardedNavigation}>
        {children}
      </UNSAFE_NavigationContext.Provider>
      <AlertDialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              The side panel has changes that are not saved yet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const action = pendingAction;
                setPendingAction(null);
                dirtyChecks.current.clear();
                action?.();
              }}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PanelControllerContext.Provider>
  );
}

/** Path string for a router `To`. */
export function toHref(to: To) {
  return typeof to === "string" ? to : createPath(to);
}
